/**
 * Disclosure Dashboard Service
 *
 * Handles all data operations for the Disclosure Dashboard feature:
 * - Fetching loans with disclosure data
 * - Computing disclosure statistics
 * - Filtering and searching
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import type {
  DisclosureLoanRow,
  DisclosureLoan,
  DisclosureFilters,
  DisclosureStats,
  DisclosureStatus,
  DisclosureExportRow
} from '@/types/disclosure-dashboard';

const log = createComponentLogger('DisclosureDashboardService', 'disclosure-dashboard');

/**
 * Calculate business days between two dates (excludes weekends)
 * Returns negative if end is before start
 */
function getBusinessDaysDiff(startDate: Date, endDate: Date): number {
  let count = 0;
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  const direction = end >= current ? 1 : -1;

  while (current.toDateString() !== end.toDateString()) {
    current.setDate(current.getDate() + direction);
    const dayOfWeek = current.getDay();
    // Skip weekends (0 = Sunday, 6 = Saturday)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      count += direction;
    }
  }

  return count;
}

/**
 * Check if LE is overdue (not sent within 3 business days of application)
 * TRID requires initial LE within 3 business days of receiving application
 */
function computeLeOverdue(row: DisclosureLoanRow): { overdue: boolean; daysSinceApp: number | null } {
  // If no application date, can't be overdue
  if (!row.application_date) {
    return { overdue: false, daysSinceApp: null };
  }

  // If LE already sent, check if it was sent on time
  if (row.le_initial_sent_date) {
    const appDate = parseAsLocalDate(row.application_date);
    const sentDate = parseAsLocalDate(row.le_initial_sent_date);
    const businessDays = getBusinessDaysDiff(appDate, sentDate);
    // Sent on time (within 3 business days)
    return { overdue: businessDays > 3, daysSinceApp: null };
  }

  // LE not sent yet - check how many business days since app
  const appDate = parseAsLocalDate(row.application_date);
  const today = new Date();
  const businessDays = getBusinessDaysDiff(appDate, today);

  return {
    overdue: businessDays > 3,
    daysSinceApp: businessDays > 0 ? businessDays : null
  };
}

/**
 * Compute the overall disclosure status for a loan
 * Uses le_itop_received_date (field 3197) for ITOP compliance tracking
 *
 * Note: Loans without application_date are filtered out before this runs
 */
function computeDisclosureStatus(row: DisclosureLoanRow): DisclosureStatus {
  // 1. CD received = complete
  if (row.cd_received_date) {
return 'complete';
}

  // 2. CD sent but not received
  if (row.cd_sent_date) {
return 'cd_sent';
}

  // 3. ITOP received (borrower signed/returned Intent to Proceed)
  if (row.le_itop_received_date) {
return 'itop_received';
}

  // 4. LE sent but no ITOP received yet
  if (row.le_initial_sent_date) {
return 'itop_missing';
}

  // 5. LE not sent - check if overdue (>3 business days since app)
  const { overdue } = computeLeOverdue(row);
  if (overdue) {
return 'le_overdue';
}

  // 6. LE not sent yet (still within 3-day window)
  return 'not_sent';
}

/**
 * Transform a raw DB row into a DisclosureLoan with computed fields
 */
function transformLoanRow(row: DisclosureLoanRow, consumerLink?: string | null): DisclosureLoan {
  const borrower_name = [row.borrower_first_name, row.borrower_last_name]
    .filter(Boolean)
    .join(' ') || 'Unknown';

  const coborrower_name = row.co_borrower_first_name
    ? [row.co_borrower_first_name, row.co_borrower_last_name].filter(Boolean).join(' ')
    : null;

  const loan_officer_name = [row.loan_officer_first_name, row.loan_officer_last_name]
    .filter(Boolean)
    .join(' ') || 'Unassigned';

  // Calculate compliance status
  const { overdue, daysSinceApp } = computeLeOverdue(row);

  // Check if audit cache is valid
  // Cache is valid when:
  // 1. Audit exists with AtApp package type
  // 2. Field hashes match (loan data hasn't changed since audit)
  const hasValidAuditCache = !!(
    row.disclosure_audit_id &&
    row.disclosure_audit_package_type === 'AtApp' &&
    row.disclosure_audit_at &&
    row.disclosure_fields_hash &&
    row.disclosure_audit_fields_hash === row.disclosure_fields_hash
  );

  // Check if document order cache is valid
  // Cache is valid when:
  // 1. Order exists and succeeded
  // 2. Field hashes match (audit is still valid)
  // 3. Parties exist (Send requires at least one borrower party)
  const hasValidOrderCache = !!(
    row.disclosure_order_id &&
    row.disclosure_order_status === 'SUCCEEDED' &&
    row.disclosure_order_documents &&
    row.disclosure_audit_fields_hash === row.disclosure_fields_hash &&
    Array.isArray(row.disclosure_order_parties) &&
    row.disclosure_order_parties.length > 0
  );

  return {
    ...row,
    borrower_name,
    coborrower_name,
    loan_officer_name,
    has_itop: !!row.le_itop_received_date,
    has_initial_le: !!row.le_initial_sent_date,
    le_received: !!row.le_initial_received_date,
    has_cd: !!row.cd_sent_date,
    cd_received: !!row.cd_received_date,
    disclosure_status: computeDisclosureStatus(row),
    consumer_link: consumerLink || null,
    le_overdue: overdue,
    days_since_app: daysSinceApp,
    has_valid_audit_cache: hasValidAuditCache,
    has_valid_order_cache: hasValidOrderCache
  };
}

class DisclosureDashboardService {
  // ============================================
  // Fetch Loans
  // ============================================

  /**
   * Fetch loans with disclosure data for the dashboard
   */
  async getDisclosureLoans(
    organizationId: string,
    filters: DisclosureFilters = { search: '', lockStatus: 'all', itopStatus: 'all', disclosureStatus: 'all' }
  ): Promise<DisclosureLoan[]> {
    try {
      const startTime = Date.now();

      // Build query
      let query = supabase
        .from('encompass_loans')
        .select(`
          id,
          encompass_loan_id,
          trans_details_loan_number,
          borrower_first_name,
          borrower_last_name,
          co_borrower_first_name,
          co_borrower_last_name,
          trans_details_loan_amt,
          trans_details_loan_purpose,
          property_address,
          subject_property_city,
          subject_property_state,
          milestone,
          loan_officer_first_name,
          loan_officer_last_name,
          channel,
          creation_date,
          application_date,
          rate_lock_status,
          rate_lock_buy_side_lock_expires,
          is_file_locked,
          file_locked_by,
          file_locked_by_name,
          file_lock_time,
          file_lock_type,
          trans_details_interest_rate,
          loan_term_months,
          trans_details_term_due_in,
          first_payment_date,
          loan_originator_name,
          nmls_loan_originator_id,
          fees_interest_from_date,
          le_itop_sent_date,
          le_itop_received_date,
          le_initial_sent_date,
          le_initial_received_date,
          le_redisclosure_sent_date,
          le_redisclosure_received_date,
          cd_sent_date,
          cd_received_date,
          cd_revised_sent_date,
          cd_revised_received_date,
          econsent_date,
          borrower_econsent_status,
          coborrower_econsent_status,
          borrower_consumer_link,
          amortization_type,
          interest_only_term_months,
          trans_details_loan_type,
          disclosure_audit_id,
          disclosure_audit_result,
          disclosure_audit_package_type,
          disclosure_audit_plan_code_id,
          disclosure_audit_at,
          disclosure_fields_hash,
          disclosure_audit_fields_hash,
          disclosure_order_id,
          disclosure_order_status,
          disclosure_order_documents,
          disclosure_order_parties,
          disclosure_preview_url,
          disclosure_preview_hash
        `)
        .eq('organization_id', organizationId)
        .or('deleted_from_encompass.is.null,deleted_from_encompass.eq.false')
        // Exclude closed/transferred folders
        .not('loan_folder', 'in', '("Transferred Data","Closed Loans","Archive","Test","Adverse Loans","(Archive)","(Trash)")')
        // Exclude brokered channel - not responsible for disclosures
        .neq('channel', 'Brokered')
        // Only include loans with application date (TRID 3-day rule only applies after application)
        .not('application_date', 'is', null);

      // Apply lock status filter
      if (filters.lockStatus !== 'all') {
        query = query.eq('rate_lock_status', filters.lockStatus);
      }

      // Apply loan officer filter
      if (filters.loanOfficerId) {
        query = query.eq('loan_officer_id', filters.loanOfficerId);
      }

      // Order by creation date descending
      query = query.order('creation_date', { ascending: false });

      const { data, error } = await query;

      if (error) {
        log.error('Failed to fetch disclosure loans', { organizationId, filters }, error);
        return [];
      }

      // Transform rows and apply client-side filters
      // Consumer link comes directly from borrower_consumer_link column
      let loans = (data || []).map(row =>
        transformLoanRow(row, (row as any).borrower_consumer_link)
      );

      // Apply disclosure status filter
      if (filters.disclosureStatus && filters.disclosureStatus !== 'all') {
        loans = loans.filter(l => l.disclosure_status === filters.disclosureStatus);
      }

      // Apply ITOP status filter (legacy - kept for backwards compatibility)
      if (filters.itopStatus === 'missing') {
        loans = loans.filter(l => l.disclosure_status === 'itop_missing');
      } else if (filters.itopStatus === 'received') {
        loans = loans.filter(l => l.has_itop);
      } else if (filters.itopStatus === 'not_applicable') {
        loans = loans.filter(l => l.disclosure_status === 'not_sent');
      }

      // Apply search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        loans = loans.filter(l =>
          l.borrower_name.toLowerCase().includes(searchLower) ||
          l.trans_details_loan_number?.toLowerCase().includes(searchLower) ||
          l.property_address?.toLowerCase().includes(searchLower) ||
          l.loan_officer_name.toLowerCase().includes(searchLower)
        );
      }

      log.info('Fetched disclosure loans', {
        organizationId,
        count: loans.length,
        duration: Date.now() - startTime
      });

      return loans;
    } catch (error) {
      log.error('Error in getDisclosureLoans', { organizationId }, error as Error);
      return [];
    }
  }

  // ============================================
  // Statistics
  // ============================================

  /**
   * Compute disclosure statistics
   */
  async getDisclosureStats(organizationId: string): Promise<DisclosureStats> {
    try {
      // Get all active loans for stats
      const { data, error } = await supabase
        .from('encompass_loans')
        .select(`
          id,
          le_itop_sent_date,
          le_itop_received_date,
          le_initial_sent_date,
          le_initial_received_date,
          cd_sent_date,
          cd_received_date,
          creation_date
        `)
        .eq('organization_id', organizationId)
        .or('deleted_from_encompass.is.null,deleted_from_encompass.eq.false')
        .not('loan_folder', 'in', '("Transferred Data","Closed Loans","Archive","Test","Adverse Loans","(Archive)","(Trash)")')
        // Exclude brokered channel - not responsible for disclosures
        .neq('channel', 'Brokered')
        // Only include loans with application date (TRID 3-day rule only applies after application)
        .not('application_date', 'is', null);

      if (error) {
        log.error('Failed to fetch stats', { organizationId }, error);
        return { totalActiveLoans: 0, itopMissing: 0, initialPackagesMtd: 0 };
      }

      const loans = data || [];

      // Calculate MTD start date
      const now = new Date();
      const mtdStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      const stats: DisclosureStats = {
        totalActiveLoans: loans.length,
        itopMissing: loans.filter(l =>
          l.le_initial_sent_date && !l.le_itop_received_date
        ).length,
        initialPackagesMtd: loans.filter(l =>
          l.le_initial_sent_date && l.le_initial_sent_date >= mtdStart
        ).length
      };

      log.info('Computed disclosure stats', { organizationId, stats });
      return stats;
    } catch (error) {
      log.error('Error in getDisclosureStats', { organizationId }, error as Error);
      return { totalActiveLoans: 0, itopMissing: 0, initialPackagesMtd: 0 };
    }
  }

  // ============================================
  // Export
  // ============================================

  /**
   * Transform loans to export format
   */
  toExportRows(loans: DisclosureLoan[]): DisclosureExportRow[] {
    const formatDate = (date: string | null): string => {
      if (!date) {
return '';
}
      try {
        return parseAsLocalDate(date).toLocaleDateString();
      } catch {
        return '';
      }
    };

    const formatCurrency = (amount: number | null): string => {
      if (amount == null) {
return '';
}
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0
      }).format(amount);
    };

    return loans.map(loan => ({
      'Loan Number': loan.trans_details_loan_number || '',
      Borrower: loan.borrower_name,
      'Co-Borrower': loan.coborrower_name || '',
      'Loan Amount': formatCurrency(loan.trans_details_loan_amt),
      'Property Address': [loan.property_address, loan.subject_property_city, loan.subject_property_state]
        .filter(Boolean)
        .join(', '),
      'Loan Purpose': loan.trans_details_loan_purpose || '',
      Milestone: loan.milestone || '',
      'Loan Officer': loan.loan_officer_name,
      'App Date': formatDate(loan.application_date),
      'Lock Status': loan.rate_lock_status || 'Not Locked',
      'Lock Expires': formatDate(loan.rate_lock_buy_side_lock_expires),
      eConsent: formatDate(loan.econsent_date),
      ITOP: formatDate(loan.le_itop_received_date),
      'LE Initial Sent': formatDate(loan.le_initial_sent_date),
      'LE Initial Received': formatDate(loan.le_initial_received_date),
      'LE Revised Sent': formatDate(loan.le_redisclosure_sent_date),
      'LE Revised Received': formatDate(loan.le_redisclosure_received_date),
      'CD Sent': formatDate(loan.cd_sent_date),
      'CD Received': formatDate(loan.cd_received_date),
      'CD Revised Sent': formatDate(loan.cd_revised_sent_date),
      'CD Revised Received': formatDate(loan.cd_revised_received_date),
      Created: formatDate(loan.creation_date)
    }));
  }

  /**
   * Export loans to CSV string
   */
  exportToCSV(loans: DisclosureLoan[]): string {
    const rows = this.toExportRows(loans);
    if (rows.length === 0) {
return '';
}

    const headers = Object.keys(rows[0]);
    const csvRows = [
      headers.join(','),
      ...rows.map(row =>
        headers.map(h => {
          const value = row[h as keyof DisclosureExportRow];
          // Escape quotes and wrap in quotes if contains comma
          if (value.includes(',') || value.includes('"')) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ];

    return csvRows.join('\n');
  }
}

export const disclosureDashboardService = new DisclosureDashboardService();
